import { defineMessages } from '@atlassian/jira-intl';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { Group } from '../../constants.tsx';

type Messages = {
	loadMoreButtonText: MessageDescriptor;
	whatIsThisContent: MessageDescriptor;
	moreInformationAboutTheFieldsButtonText: MessageDescriptor;
	suggestedGroupButtonText: MessageDescriptor;
	moreInformationAboutTheSuggestedFieldsButtonText: MessageDescriptor;
	userDoesntHaveEditLayoutPermissionMessage: MessageDescriptor;
	screenNotIsolatedMessage: MessageDescriptor;
	userDoesntHaveEditLayoutPermissionMessageIssueTermRefresh: MessageDescriptor;
	screenNotIsolatedMessageIssueTermRefresh: MessageDescriptor;
	emptyStateAltText: MessageDescriptor;
} & Record<Group, MessageDescriptor>;
const messages: Messages = defineMessages({
	whatIsThisContent: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.what-is-this-content',
		defaultMessage:
			"Fields capture and display the information people need to carry out a project's work.",
		description: 'Explanation text inside tooltip',
	},
	loadMoreButtonText: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.load-more-button-text',
		defaultMessage: 'Show next {count, plural, one {# field} other {# fields}}',
		description: 'Button that loads more items',
	},
	moreInformationAboutTheFieldsButtonText: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.more-information-about-the-fields-button-text',
		defaultMessage: 'More information about the fields',
		description: 'Button that shows more details about available items',
	},
	suggestedGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.suggested-group',
		defaultMessage: 'Suggested fields',
		description: 'Group title displayed for suggested fields within the sidebar',
	},
	assetsGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.assets-group',
		defaultMessage: 'Asset fields',
		description: 'Group title displayed for assets fields within the sidebar',
	},
	datesGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.dates-group',
		defaultMessage: 'Date and time fields',
		description: 'Group title displayed for dates fields within the sidebar',
	},
	labelsGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.labels-group',
		defaultMessage: 'Label fields',
		description: 'Group title displayed for labels fields within the sidebar',
	},
	numberGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.number-group',
		defaultMessage: 'Number fields',
		description: 'Group title displayed for number fields within the sidebar',
	},
	peopleGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.people-group',
		defaultMessage: 'People and groups fields',
		description: 'Group title displayed for people fields within the sidebar',
	},
	selectGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.select-group',
		defaultMessage: 'Select fields',
		description: 'Group title displayed for select fields within the sidebar',
	},
	systemGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.system-group',
		defaultMessage: 'System fields',
		description: 'Group title displayed for system fields within the sidebar',
	},
	textGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.text-group',
		defaultMessage: 'Text fields',
		description: 'Group title displayed for text fields within the sidebar',
	},
	versionGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.version-group',
		defaultMessage: 'Version fields',
		description: 'Group title displayed for version fields within the sidebar',
	},
	otherGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.other-group',
		defaultMessage: 'Other fields',
		description: 'Group title displayed for other (ungrouped) fields within the sidebar',
	},
	advancedGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.advanced-group',
		defaultMessage: 'Advanced fields',
		description: 'Group title displayed for advanced fields within the sidebar',
	},
	restrictedGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.restricted-group',
		defaultMessage: 'Restricted fields',
		description: 'Group title displayed for restricted fields within the sidebar',
	},
	aiSuggestedGroup: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.ai-suggested-group',
		defaultMessage: 'AI suggested fields',
		description: 'Group title displayed for AI suggested fields within the sidebar',
	},
	suggestedGroupButtonText: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.suggested-group-button-text',
		defaultMessage: 'What are suggested fields?',
		description: 'Button that shows more details about suggested fields',
	},
	moreInformationAboutTheSuggestedFieldsButtonText: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.more-information-about-the-suggested-fields-button-text',
		defaultMessage: 'More information about the suggested fields',
		description: 'Button that shows more details about available items',
	},
	userDoesntHaveEditLayoutPermissionMessage: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.user-doesnt-have-edit-layout-permission-message',
		defaultMessage:
			'You need the ‘Manage Issue Layouts’ permission to add fields to an issue layout - contact your Jira Admin.',
		description: 'Explanation text for non-admin users without edit layout permission',
	},
	screenNotIsolatedMessage: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.screen-not-isolated-message',
		defaultMessage:
			'The screen supporting this issue layout is shared by multiple projects, so only Jira Admins can add fields.',
		description:
			'Explanation text for non-admin users with edit layout permission, but the screen is used by multiple projects',
	},
	userDoesntHaveEditLayoutPermissionMessageIssueTermRefresh: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.user-doesnt-have-edit-layout-permission-message-issue-term-refresh',
		defaultMessage:
			'You need the ‘Manage work item layouts’ permission to add fields to a work item - contact your Jira Admin.',
		description: 'Explanation text for non-admin users without edit layout permission',
	},
	screenNotIsolatedMessageIssueTermRefresh: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.screen-not-isolated-message-issue-term-refresh',
		defaultMessage:
			'The screen supporting the layout of this work item is shared by multiple projects, so only Jira Admins can add fields.',
		description:
			'Explanation text for non-admin users with edit layout permission, but the screen is used by multiple projects',
	},
	emptyStateAltText: {
		id: 'project-settings-apps-issue-type-page-product-features.common.sidebar.empty-state-alt-text',
		defaultMessage: 'This sidebar supports drag-and-drop fields.',
		description: 'Alt text for empty state image on drag and drop field sidebar',
	},
});
export default messages;
